import type { Store } from "@/data/repositories";
import type { Clock } from "@/domain/clock";
import { isValidTime, todayKey } from "@/domain/dates";
import type { Scenario, Subscription } from "@/domain/entities";
import { newId } from "@/domain/ids";

export type SubscriptionPatch = Partial<Pick<Subscription, "reminderTime" | "enabled" | "settings">>;

export class SubscriptionService {
  constructor(
    private readonly store: Store,
    private readonly clock: Clock,
  ) {}

  async subscribe(scenario: Scenario, reminderTime: string): Promise<Subscription> {
    this.assertTime(reminderTime);
    const now = this.clock.now().toISOString();
    const subscription: Subscription = {
      id: newId(),
      scenarioId: scenario.id,
      scenarioVersion: scenario.version,
      reminderTime,
      enabled: true,
      startedAt: todayKey(this.clock),
      createdAt: now,
      settings: {},
      updatedAt: now,
      deleted: false,
    };
    await this.store.subscriptions.put(subscription);
    return subscription;
  }

  async update(id: string, patch: SubscriptionPatch): Promise<Subscription> {
    const current = await this.store.subscriptions.get(id);
    if (!current) throw new Error(`Unknown subscription: ${id}`);
    if (patch.reminderTime !== undefined) this.assertTime(patch.reminderTime);
    const next: Subscription = { ...current, ...patch, updatedAt: this.clock.now().toISOString() };
    await this.store.subscriptions.put(next);
    return next;
  }

  async unsubscribe(id: string): Promise<void> {
    await this.store.subscriptions.remove(id);
  }

  async listActive(): Promise<Subscription[]> {
    return (await this.store.subscriptions.listAll()).filter((s) => s.enabled);
  }

  private assertTime(time: string): void {
    if (!isValidTime(time)) throw new Error(`Invalid reminder time: ${time}`);
  }
}
